import React, { useState } from 'react';
import { Link, usePage, router } from '@inertiajs/react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Calendar,
  Clock,
  Plus,
  CheckCircle2,
  XCircle,
  FileText,
  ArrowLeft,
  Edit,
  Trash2,
  AlertTriangle,
} from 'lucide-react';
import { format } from 'date-fns';

interface Session {
  id: number;
  session_date: string;
  duration_minutes: number;
  occurred: boolean;
  discussion_summary: string;
  mentee_todos: string | null;
  next_session_date: string | null;
  created_at: string;
}

interface Match {
  id: number;
  mentor: {
    id: number;
    first_name: string;
    last_name: string;
  };
  mentee: {
    id: number;
    first_name: string;
    last_name: string;
  };
}

interface Props {
  match: Match;
  sessions: Session[];
}

const SessionCard = ({
  session,
  match,
  isMentor,
}: {
  session: Session;
  match: Match;
  isMentor: boolean;
}) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = () => {
    setIsDeleting(true);
    router.delete(`/matches/${match.id}/mentorship_sessions/${session.id}`, {
      onFinish: () => {
        setIsDeleting(false);
        setShowDeleteDialog(false);
      },
    });
  };

  return (
    <>
      <Card className="border-0 shadow-sm hover:shadow-md transition-all pt-0 pb-6">
        <CardHeader className="border-b border-gray-100 bg-gray-50/50 pt-6">
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-lg font-medium flex items-center gap-2">
                <Calendar className="h-4 w-4 text-gray-400" />
                {format(new Date(session.session_date), 'MMMM d, yyyy')}
              </CardTitle>
              <CardDescription className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                <Clock className="h-3 w-3" />
                {session.duration_minutes} minutes
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={session.occurred ? 'default' : 'destructive'}>
                {session.occurred ? (
                  <>
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Completed
                  </>
                ) : (
                  <>
                    <XCircle className="h-3 w-3 mr-1" />
                    Cancelled
                  </>
                )}
              </Badge>
              {isMentor && (
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    asChild
                    className="h-8 w-8"
                  >
                    <Link
                      href={`/matches/${match.id}/mentorship_sessions/${session.id}/edit`}
                    >
                      <Edit className="h-4 w-4" />
                    </Link>
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowDeleteDialog(true)}
                    className="h-8 w-8 text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-2">
              Discussion Summary
            </h4>
            <p className="text-sm text-gray-700">
              {session.discussion_summary}
            </p>
          </div>

          {session.mentee_todos && (
            <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
              <h4 className="text-xs uppercase tracking-wider text-blue-700 font-medium mb-2">
                Mentee TODOs
              </h4>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                {session.mentee_todos}
              </p>
            </div>
          )}

          {session.next_session_date && (
            <div className="pt-4 border-t border-gray-100">
              <p className="text-sm text-muted-foreground">
                Next session:{' '}
                {format(new Date(session.next_session_date), 'MMMM d, yyyy')}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete Session
            </DialogTitle>
            <DialogDescription className="pt-3 space-y-2">
              <p>Are you sure you want to delete this session?</p>
              <div className="bg-muted p-3 rounded-md space-y-1 text-sm">
                <p className="font-medium">
                  {format(new Date(session.session_date), 'MMMM d, yyyy')}
                </p>
                <p className="text-muted-foreground">
                  {session.duration_minutes} minute session
                </p>
              </div>
              <p className="text-sm text-destructive font-medium">
                This action cannot be undone.
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete Session'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const EmptyState = ({ matchId }: { matchId: number }) => (
  <Card className="border-0 shadow-sm">
    <CardContent className="flex flex-col items-center justify-center py-16">
      <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
        <FileText className="h-8 w-8 text-gray-400" />
      </div>
      <h3 className="text-xl font-medium text-gray-900 mb-2">
        No Sessions Yet
      </h3>
      <p className="text-sm text-gray-500 mb-6 text-center max-w-sm">
        Start logging your mentorship sessions to track progress and maintain a
        record of your meetings.
      </p>
      <Button asChild>
        <Link href={`/matches/${matchId}/mentorship_sessions/new`}>
          <Plus className="h-4 w-4 mr-2" />
          Log First Session
        </Link>
      </Button>
    </CardContent>
  </Card>
);

export default function MentorshipSessionsIndex({ match, sessions }: Props) {
  const { current_user } = usePage().props as any;
  const isMentor = current_user.id === match.mentor.id;

  // Calculate statistics
  const totalSessions = sessions.filter((s) => s.occurred).length;
  const totalHours =
    sessions
      .filter((s) => s.occurred)
      .reduce((sum, s) => sum + s.duration_minutes, 0) / 60;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Button variant="ghost" size="sm" asChild>
              <Link href="/matches/dashboard">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Link>
            </Button>
          </div>

          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Session History
              </h1>
              <p className="text-gray-600">
                {match.mentor.first_name} {match.mentor.last_name} mentoring{' '}
                {match.mentee.first_name} {match.mentee.last_name}
              </p>
            </div>

            {isMentor && (
              <Button asChild>
                <Link href={`/matches/${match.id}/mentorship_sessions/new`}>
                  <Plus className="h-4 w-4 mr-2" />
                  Log New Session
                </Link>
              </Button>
            )}
          </div>
        </div>

        {/* Statistics */}
        <div className="grid gap-6 md:grid-cols-2 mb-8">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">
                    Total Sessions
                  </p>
                  <p className="text-4xl font-light">{totalSessions}</p>
                  <div className="h-1 w-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full" />
                </div>
                <CheckCircle2 className="h-5 w-5 text-purple-500 mt-1" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">
                    Total Hours
                  </p>
                  <p className="text-4xl font-light">{totalHours.toFixed(1)}</p>
                  <div className="h-1 w-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full" />
                </div>
                <Clock className="h-5 w-5 text-blue-500 mt-1" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sessions List */}
        <div className="space-y-4">
          <div>
            <h2 className="text-2xl font-medium text-gray-900 mb-2">
              All Sessions
            </h2>
            <p className="text-sm text-gray-500">
              Complete history of your mentorship sessions
            </p>
          </div>

          {sessions.length > 0 ? (
            <div className="space-y-4">
              {sessions.map((session) => (
                <SessionCard
                  key={session.id}
                  session={session}
                  match={match}
                  isMentor={isMentor}
                />
              ))}
            </div>
          ) : (
            <EmptyState matchId={match.id} />
          )}
        </div>
      </div>
    </div>
  );
}
